import { useShopContext } from "../../../context/shop/shop.context";
import { useForm, SubmitHandler } from "react-hook-form";
import {
  ShippingMethodIdEnum,
  ShopActionType,
} from "../../../context/shop/shop.types";

type ShippingAddress = {
  shipping_first_name: string;
  shipping_last_name: string;
  shipping_company: string;
  shipping_address_1: string;
  shipping_address_2: string;
  shipping_city: string;
  shipping_state: string;
  shipping_postcode: string;
  shipping_country: string;
};
type BillingAddress = {
  billing_first_name: string;
  billing_last_name: string;
  billing_company: string;
  billing_address_1: string;
  billing_address_2: string;
  billing_city: string;
  billing_state: string;
  billing_postcode: string;
  billing_country: string;
  billing_email: string;
  billing_phone: string;
};
type FormInputs = ShippingAddress &
  BillingAddress & {
    shippingMethodId: ShippingMethodIdEnum;
    isDifferentAddress: boolean;
  };

export const useShippingForm = (handleNext: () => void) => {
  const { shopState, shopDispatch, shippingMethods } = useShopContext();
  const { register, handleSubmit, watch, formState } = useForm<FormInputs>({
    defaultValues: {
      shipping_first_name: shopState.shippingAddress.first_name,
      shipping_last_name: shopState.shippingAddress.last_name,
      shipping_company: shopState.shippingAddress.company,
      shipping_address_1: shopState.shippingAddress.address_1,
      shipping_address_2: shopState.shippingAddress.address_2,
      shipping_city: shopState.shippingAddress.city,
      shipping_state: shopState.shippingAddress.state,
      shipping_postcode: shopState.shippingAddress.postcode,
      shipping_country: shopState.shippingAddress.country,
      billing_first_name: shopState.billingAddress.first_name,
      billing_last_name: shopState.billingAddress.last_name,
      billing_company: shopState.billingAddress.company,
      billing_address_1: shopState.billingAddress.address_1,
      billing_address_2: shopState.billingAddress.address_2,
      billing_city: shopState.billingAddress.city,
      billing_state: shopState.billingAddress.state,
      billing_postcode: shopState.billingAddress.postcode,
      billing_country: shopState.billingAddress.country,
      billing_email: shopState.billingAddress.email,
      billing_phone: shopState.billingAddress.phone,
      shippingMethodId: shopState.shippingMethodId,
      isDifferentAddress: shopState.isDifferentAddress,
    },
  });

  const onSubmit: SubmitHandler<FormInputs> = ({
    shippingMethodId,
    isDifferentAddress,
    ...addresses
  }) => {
    const { email, phone, ...billingAddress } = {
      first_name: addresses.billing_first_name,
      last_name: addresses.billing_last_name,
      company: addresses.billing_company,
      address_1: addresses.billing_address_1,
      address_2: addresses.billing_address_2,
      city: addresses.billing_city,
      state: addresses.billing_state,
      postcode: addresses.billing_postcode,
      country: addresses.billing_country,
      email: addresses.billing_email,
      phone: addresses.billing_phone,
    };
    const shippingAddress = !isDifferentAddress
      ? billingAddress
      : {
          first_name: addresses.shipping_first_name,
          last_name: addresses.shipping_last_name,
          company: addresses.shipping_company,
          address_1: addresses.shipping_address_1,
          address_2: addresses.shipping_address_2,
          city: addresses.shipping_city,
          state: addresses.shipping_state,
          postcode: addresses.shipping_postcode,
          country: addresses.shipping_country,
        };
    shopDispatch({
      type: ShopActionType.SET_STATE,
      payload: {
        shippingMethodId,
        shippingAddress,
        isDifferentAddress,
        billingAddress: { ...billingAddress, phone, email },
      },
    });
    handleNext();
  };

  return { register, watch, onSubmit: handleSubmit(onSubmit), formState };
};
